import crossFetch from 'cross-fetch';
import * as React from 'react';
import { ContainerElement, PlaceholderElement, getMfInstanceId, getMfServerCookie, getMfServerOverride, } from '../lib/mf-utils.js';
import { getRequestContext, serializeServerProps } from '../ssr-bridge/index.js';
import { initializeHost } from '../state/index.js';
import { getGlobalThis, isServer, loadStyle } from '../utils/index.js';
import { useIsomorphicData } from './isomorphic-data-hook.js';
import { isDevLikeEnv } from './utils.js';
const fetch = getGlobalThis().fetch ? getGlobalThis().fetch : crossFetch;
/**
 * Microfrontend client to load and render a microfrontend.
 *
 * Note:
 * 'mfServer' value can be overridden by setting it to 'CRYSTAL_MF_SERVER_{mfId}' cookie
 */
export const MicrofrontendClient = props => {
    // initialize the Crystal host info if not already
    React.useMemo(() => {
        initializeHost({
            context: props.context,
        });
    }, []);
    const { mfId, mfConfig, mfProps, context, instanceId, children, onError, sameHost, noPrerender } = props;
    const ErrorView = props.errorView;
    const mfServer = getMfServerOverride(props.mfServer, {
        mfId,
        preventOverride: context.env === 'prod',
    });
    const mfInfo = {
        mfId,
        mfServer,
        context,
    };
    const serverProps = {
        mfProps: {
            ...mfInfo,
            ...mfProps,
        },
        renderProps: {
            ...mfInfo,
            instanceId,
            mfType: 'component',
            styleDependencies: props.loadStyleDependencies,
        },
    };
    const renderUrl = getRenderUrl(serverProps, sameHost);
    const { data, error } = useIsomorphicData({
        id: getPrerenderDataKey(mfId, context, instanceId),
        type: noPrerender ? 'clientOnly' : 'prerender',
    }, () => {
        return isServer()
            ? renderOnServer(renderUrl, mfInfo)
            : renderOnBrowser(renderUrl, mfInfo, mfConfig, props.loadStyleDependencies);
    }, () => {
        const container = new ContainerElement(mfId, instanceId).query();
        if (container && container.querySelector(PlaceholderElement.selector)) {
            return container.innerHTML;
        }
    });
    const mfHtml = typeof data === 'object' ? data.html : data;
    const mfModule = typeof data === 'object' ? data.mf : undefined;
    React.useEffect(() => {
        if (!mfModule) {
            return;
        }
        mfModule.hydrate(mfId, instanceId);
        const placeholder = new PlaceholderElement(mfId, instanceId).query();
        return () => {
            if (placeholder) {
                mfModule.unmount(placeholder);
            }
        };
    }, [mfModule]);
    const container = new ContainerElement(mfId, instanceId);
    if (error) {
        onError && onError(error);
        return (React.createElement("div", { ...container.attrs() }, ErrorView ? React.createElement(ErrorView, { error: error }) : React.createElement(DefaultErrorView, { error: error, env: context.env })));
    }
    return mfHtml ? (React.createElement("div", { ...container.attrs(), dangerouslySetInnerHTML: { __html: mfHtml } })) : (React.createElement("div", { ...container.attrs() }, children));
};
const DefaultErrorView = ({ env, error }) => {
    if (error instanceof Error && isDevLikeEnv(env)) {
        return React.createElement("div", null, error.message);
    }
    return React.createElement("div", null, "Sorry, unknown error occurred.");
};
function getRenderUrl({ mfProps, renderProps }, sameHost) {
    const { mfId, mfServer, context } = renderProps;
    const paramEntries = serializeServerProps({
        renderProps,
        mfProps,
    });
    const params = new URLSearchParams(paramEntries);
    const localServer = isServer() && sameHost ? getRequestContext()?.localhost : undefined;
    return `${localServer || mfServer}/${mfId}/view/${context.brand}/${context.locale}?${params.toString()}`;
}
async function renderOnServer(renderUrl, mfInfo) {
    const resp = await fetch(renderUrl, {
        headers: {
            Cookie: getRequestContext()?.headers.cookie || '',
        },
    });
    await validateResponse(renderUrl, resp, mfInfo);
    return resp.text();
}
async function renderOnBrowser(renderUrl, mfInfo, mfConfig, loadStyleDependencies) {
    const resp = await fetch(renderUrl, {
        credentials: getRequestCookieOption(mfInfo),
    });
    await validateResponse(renderUrl, resp, mfInfo);
    const mfVersion = resp.headers.get('X-Mf-Version');
    const [html, mfModule] = await Promise.all([
        resp.text(),
        loadMfModule(mfInfo, mfVersion),
        loadMfStyles(mfInfo, mfVersion, mfConfig, loadStyleDependencies),
    ]);
    return {
        html,
        mf: mfModule,
    };
}
async function loadMfModule({ mfId, mfServer }, mfVersion) {
    const { default: mf } = await import(
    /* @vite-ignore */
    /* webpackIgnore: true */
    `${mfServer}/${mfId}/index.js?v=${mfVersion}`);
    return mf;
}
async function loadMfStyles({ mfId, mfServer, context }, mfVersion, mfConfig, loadStyleDependencies) {
    const mfStyleUrl = `${mfServer}/${mfId}/style.css?v=${mfVersion}`;
    const mfStyles = [mfStyleUrl];
    if (loadStyleDependencies && mfConfig?.styleDependencies) {
        const commonStyles = mfConfig.styleDependencies['*'] || [];
        const brandStyles = mfConfig.styleDependencies[context.brand] || [];
        mfStyles.push(...commonStyles.concat(brandStyles));
    }
    await Promise.all(mfStyles.map(style => loadStyle(style, style).catch(() => undefined)));
}
export function getPrerenderDataKey(mfId, context, instanceId) {
    return `prerender_${getMfInstanceId(mfId, instanceId)}-${context.brand}-${context.locale}`;
}
function validateResponse(renderUrl, resp, { mfId }) {
    if (!resp.ok) {
        return Promise.reject(new Error(`Error fetching mf: ${renderUrl}`));
    }
    const respMfId = resp.headers.get('X-MF-Id');
    const respMfType = resp.headers.get('X-MF-Type');
    if (respMfId !== mfId) {
        return Promise.reject(new Error(`Mismatched 'X-MF-Id' header. Rendered view is not intended for: '${mfId}'.`));
    }
    if (respMfType !== 'component') {
        return Promise.reject(new Error(`Mismatched 'X-MF-Type' header. Microfrontend: '${mfId}' must be rerendered as 'component'.`));
    }
}
const cookieOptions = ['same-origin', 'include', 'omit'];
function getRequestCookieOption({ mfId, context }) {
    if (context.env !== 'prod') {
        const value = getMfServerCookie(`${mfId}_cookie`) || getMfServerCookie('cookie');
        return cookieOptions.find(o => o === value);
    }
}
//# sourceMappingURL=MicrofrontendSSRClient.js.map